import React, { useState, useEffect } from 'react';
import { App, Input, Switch, DatePicker, Popconfirm, Button } from 'antd';
import { HolderOutlined, DownOutlined, DeleteOutlined, ClockCircleOutlined, EnvironmentOutlined, ShopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import api, { errorCode } from './api';
import { linkStatus } from './LinkRow';
import { BLOCK_EDITORS, isBlockValid } from './BlockEditors';
import { openState } from '../utils/blocks';
import { enums } from '../config';

export const BLOCK_ICONS = { hours: ClockCircleOutlined, location: EnvironmentOutlined, menu: ShopOutlined };

// One line under the title saying what the block holds
const blockSummary = (link, t) => {
  const data = link.data || {};
  if (link.kind === 'hours') {
    const state = openState(data);
    // "Kapalı" alone read as if the block itself were switched off
    return state && state.open ? t('dash.hoursOpenNow') : t('dash.hoursClosedNow');
  }
  if (link.kind === 'location') return data.address;
  if (link.kind === 'menu') return t('blocks.itemCount', { count: (data.items || []).length });
  return '';
};

const BlockRow = ({ link, username, save, dragHandleProps, expanded, onToggle }) => {
  const [t] = useTranslation();
  const { message } = App.useApp();
  const [title, setTitle] = useState(link.linkTitle);
  const [draft, setDraft] = useState(link.data);
  const [saving, setSaving] = useState(false);

  // Take the server's version whenever it changes (e.g. after a save)
  useEffect(() => { setTitle(link.linkTitle); }, [link.linkTitle]);
  useEffect(() => { setDraft(link.data); }, [link.data]);

  const send = (changes) => save(api.editLink(username, {
    _id: link._id,
    linkTitle: link.linkTitle,
    isActive: link.isActive !== false,
    scheduledStart: link.scheduledStart || null,
    scheduledEnd: link.scheduledEnd || null,
    ...changes
  }));

  const fail = err => message.error(errorCode(err) === enums.errorCodes.c14 ? t('schedule.invalid') : t('dash.saveFailed'));
  const update = changes => send(changes).catch(fail);

  const dirty = JSON.stringify(draft) !== JSON.stringify(link.data) || title.trim() !== link.linkTitle;
  const valid = isBlockValid(link.kind, draft) && !!title.trim();

  const saveBlock = () => {
    setSaving(true);
    send({ data: draft, linkTitle: title.trim() }).then(() => message.success(t('saved'))).catch(fail).finally(() => setSaving(false));
  };

  const Icon = BLOCK_ICONS[link.kind];
  const Editor = BLOCK_EDITORS[link.kind];
  const status = linkStatus(link);

  return (
    <div className={`dash-link ${expanded ? 'is-open' : ''} ${status !== 'live' ? 'is-off' : ''}`}>
      <div className="dash-link__row">
        <span className="dash-link__handle" {...dragHandleProps} aria-label={t('dash.dragToReorder')} title={t('dash.dragToReorder')}><HolderOutlined /></span>
        <button type="button" className="dash-link__summary" onClick={onToggle} aria-expanded={expanded}>
          <span className="dash-link__icon dash-link__icon--block"><Icon /></span>
          <span className="dash-link__text">
            <span className="dash-link__title">{link.linkTitle}</span>
            <span className="dash-link__url">{blockSummary(link, t)}</span>
          </span>
          {status !== 'live' && <span className={`dash-tag dash-tag--${status}`}>{t(`schedule.status.${status}`)}</span>}
        </button>
        <Switch size="small" checked={link.isActive !== false} onChange={isActive => update({ isActive })} aria-label={t('schedule.visible')} title={t('schedule.visible')} />
        <button type="button" className="dash-link__chevron" onClick={onToggle} aria-label={t('dash.details')} aria-expanded={expanded} tabIndex={-1}><DownOutlined /></button>
      </div>

      {expanded &&
        <div className="dash-link__body">
          <label className="dash-field">
            <span>{t('blocks.blockTitle')}</span>
            <Input value={title} maxLength={80} onChange={e => setTitle(e.target.value)} />
          </label>
          <Editor value={draft} onChange={setDraft} />
          {dirty &&
            <div className="dash-add__actions">
              <Button onClick={() => { setDraft(link.data); setTitle(link.linkTitle); }}>{t('dash.cancel')}</Button>
              <Button type="primary" loading={saving} disabled={!valid} onClick={saveBlock}>{t('save')}</Button>
            </div>}
          <div className="dash-field">
            <span>{t('dash.schedule')}</span>
            <DatePicker.RangePicker
              showTime={{ format: 'HH:mm' }} format="DD.MM.YYYY HH:mm" allowEmpty={[true, true]}
              placeholder={[t('schedule.start'), t('schedule.end')]}
              value={[link.scheduledStart ? dayjs(link.scheduledStart) : null, link.scheduledEnd ? dayjs(link.scheduledEnd) : null]}
              onChange={dates => {
                const [start, end] = dates || [null, null];
                update({ scheduledStart: start ? start.toISOString() : null, scheduledEnd: end ? end.toISOString() : null });
              }} />
            <span className="dash-hint">{t('dash.scheduleHint')}</span>
          </div>
          <div className="dash-link__foot">
            <span />
            <Popconfirm title={t('dash.deleteBlockConfirm')} okText={t('dash.delete')} cancelText={t('dash.cancel')} okButtonProps={{ danger: true }}
              onConfirm={() => save(api.deleteLink(username, link._id)).catch(() => message.error(t('dash.saveFailed')))}>
              <Button danger type="text" icon={<DeleteOutlined />}>{t('dash.delete')}</Button>
            </Popconfirm>
          </div>
        </div>
      }
    </div>
  );
};

export default BlockRow;
